import type { Client } from '../client';

export type PlalistItemContenType =
    'live' | 'video_importer' | 'live_recording' | 'live_timelapse' | 'video';

export type PlaylistLogoPosition =
    'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export type PlaylistLogo = {
    image: string;
    position: PlaylistLogoPosition;
    width: string;
    margin: string;
};

export type PlaylistItem = {
    playlist_revision?: string;
    content_id: string;
    content_type: PlalistItemContenType;
    content_url: string | null;
    playback_sec: number;
    enable_audio: boolean;
};

export type PlaylistItemContent = {
    playlist_revision?: string;
    id: string;
    type: string; // live or vod
    camera_id?: string | null; // camera owning a recording or timelapse
    index: number;
    metadata: Record<string, any>;
    source: {
        src: string;
        type: string;
    };
    playback_sec: number;
    enable_audio: boolean;
};

export type Playlist = {
    name: string;
    description: string;
    resolution: number;
    template_name: string;
    logo?: PlaylistLogo;
    display_safearea: boolean | undefined;
    playlist: PlaylistItem[];
    map_detail?: string;
    playlist_revision?: string;
    audio_url: string;
    sse_enabled?: boolean; // receive playlist commands from the API
};

export class PlaylistClass {
    client: Client;
    playlist: Playlist = {
        name: '',
        description: '',
        resolution: 720,
        template_name: '',
        display_safearea: false,
        playlist: [],
        audio_url: '',
    };
    nextItem: number = 0;
    errItem: number = 0;
    callback: any;
    private retryAt = 0;
    private refreshRequest: Promise<void> | null = null;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(client: Client) {
        this.client = client;
    }

    async sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async refresh(): Promise<void> {
        if (this.refreshRequest) return this.refreshRequest;
        this.refreshRequest = this.load();
        try {
            await this.refreshRequest;
        } finally {
            this.refreshRequest = null;
        }
    }

    private async load(): Promise<void> {
        if (this.refreshTimer !== null) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        try {
            const playlist = await this.client.fetchPlaylist();
            if (playlist.playlist.length === 0) {
                console.error(`playlist is empty`);
            }

            if (playlist.playlist.length === 1) {
                console.log('ping');
            }

            this.playlist = playlist;
            console.log(
                `playlist updated with ${playlist.playlist.length} elements`,
            );

            if (typeof this.callback == 'function') {
                this.callback(playlist);
            }
        } catch (error) {
            console.error(`failed to fetch playlist: ${error}`);
        }

        // Update playlist every 30 seconds
        this.refreshTimer = setTimeout(() => {
            this.refresh();
        }, 30000);
    }

    async getNextContent(): Promise<PlaylistItemContent | null> {
        do {
            const currentPlaylist = this.playlist;
            const length = currentPlaylist.playlist.length;

            if (length === 0) {
                console.warn(`playlist is empty`);
                await this.sleep(1000);
                if (this.playlist !== currentPlaylist) continue;
                return null;
            }

            if (this.nextItem >= length) {
                this.nextItem = 0;
            }

            const index = this.nextItem;
            const item = currentPlaylist.playlist[index];

            this.nextItem++;

            try {
                const content = await this.client.getContent(
                    this.contentRequest(currentPlaylist, index),
                );

                this.errItem = 0;
                this.retryAt = 0;

                return content;
            } catch (error) {
                // 409: the playlist changed on the server. Every item of the
                // loaded playlist carries the outdated revision, so reload it
                // right away and retry the same position instead of trying
                // the remaining items until the next periodic refresh.
                if ((error as { status?: number })?.status === 409) {
                    console.warn(
                        `playlist revision ${item.playlist_revision} is outdated, reloading playlist`,
                    );
                    await this.refresh();
                    if (
                        this.playlist.playlist[0]?.playlist_revision !==
                        item.playlist_revision
                    ) {
                        this.nextItem = index;
                        continue;
                    }
                }

                // 404: the API has no playable source for this item. Other
                // failures (API errors, revision reloads) are not content failures.
                if ((error as { status?: number })?.status === 404) {
                    this.client.sendItemResult({
                        playlist_revision: item.playlist_revision,
                        index,
                        status: 'failed',
                        reason_code: 'playback.source_unavailable',
                        observed_at: new Date().toISOString(),
                    });
                }

                this.errItem++;
                console.warn(
                    `content ${item.content_id} (${item.content_type}) could not be loaded: ${error}`,
                );

                // Every item failed in a row: all callers wait before the next
                // round, 2, 4, 8 and 16 seconds, then 30 seconds per round.
                if (this.errItem % length === 0) {
                    const wait = Math.min(
                        1000 * 2 ** (this.errItem / length),
                        30000,
                    );
                    console.warn(
                        `no playlist item could be loaded, retrying in ${wait / 1000}s`,
                    );
                    this.retryAt = Date.now() + wait;
                }
            }

            await this.sleep(Math.max(1000, this.retryAt - Date.now()));
        } while (true);
    }

    private contentRequest(playlist: Playlist, index: number) {
        const item = playlist.playlist[index];
        return {
            playlist_revision: item.playlist_revision,
            content_id: item.content_id,
            content_type: item.content_type,
            content_url: item.content_url,
            index,
            playback_sec: item.playback_sec,
            resolution: playlist.resolution,
            enable_audio: item.enable_audio,
        };
    }

    // Loads one position for a manual switch, outside the automatic sequence.
    async getContentAt(index: number): Promise<PlaylistItemContent | null> {
        const currentPlaylist = this.playlist;
        const item = currentPlaylist.playlist[index];
        if (!item) return null;

        try {
            return await this.client.getContent(
                this.contentRequest(currentPlaylist, index),
            );
        } catch (error) {
            const status = (error as { status?: number })?.status;
            // The command referred to an outdated revision: reload, do not retry.
            if (status === 409) await this.refresh();
            if (status === 404) {
                this.client.sendItemResult({
                    playlist_revision: item.playlist_revision,
                    index,
                    status: 'failed',
                    reason_code: 'playback.source_unavailable',
                    observed_at: new Date().toISOString(),
                });
            }
            console.warn(
                `content ${item.content_id} (${item.content_type}) at position ${index} could not be loaded: ${error}`,
            );
            return null;
        }
    }

    // The automatic sequence continues with the position after this one.
    continueAfter(index: number): void {
        this.nextItem = index + 1;
    }

    revision(): string | undefined {
        return (
            this.playlist.playlist_revision ??
            this.playlist.playlist[0]?.playlist_revision
        );
    }

    onNewPlaylist(callback: any): void {
        this.callback = callback;
    }

    hasAudio(): boolean {
        return this.playlist.audio_url.length != 0;
    }

    getAudioUrl(): string {
        return this.playlist.audio_url;
    }
}
